/**
 * Garde de route par permission.
 *
 * Comme `RequireAuth`, elle **ne protège rien** : elle évite d'afficher un
 * écran dont tous les appels d'API répondraient 403 (docs/07 §8). Chaque route
 * du back-office est gardée côté serveur par `require_permission`, et chaque
 * cas d'usage revérifie ce qui le concerne.
 */
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

import { RequireAuth, usePermissions } from "@/features/auth";
import { Alert } from "@/shared/ui/Alert";

interface RequirePermissionProps {
  anyOf: string[];
  children: ReactNode;
}

export function RequirePermission({ anyOf, children }: RequirePermissionProps) {
  return (
    <RequireAuth>
      <PermissionGate anyOf={anyOf}>{children}</PermissionGate>
    </RequireAuth>
  );
}

/**
 * Séparé pour que `usePermissions()` ne soit lu qu'une fois l'authentification
 * établie : appelé au-dessus de `RequireAuth`, il verrait une session encore
 * vide pendant la restauration silencieuse et refuserait à tort.
 */
function PermissionGate({ anyOf, children }: RequirePermissionProps) {
  const { t } = useTranslation("common");
  const { canAny } = usePermissions();

  if (!canAny(...anyOf)) {
    return <Alert tone="error">{t("errors.permission_denied")}</Alert>;
  }
  return <>{children}</>;
}
